import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalActions, ModalBody } from 'components/ui/modal';
import Label from 'components/v2/forms/Label';
import { FileUploader } from 'components/v2/FileUploader';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { useTranslation } from 'react-i18next';
import Alert from 'components/ui/Alert';
import { uploadCSVCreateLaneRates } from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';

export const rowErrorHeader = (row, t) =>
  row === 'global' ? t('ERROR') : t('ROW') + ' ' + row;

export const LaneRatesCsvUploadModal = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();
  const fileUploaderRef = useRef(null);

  async function uploadLaneRates() {
    try {
      setLoading(true);
      setErrors(null);

      await uploadCSVCreateLaneRates(files[0]);

      showToast(t('CSV_UPLOAD_SUCCESSFUL'), null, 'success');
      close();
    } catch (e) {
      if (!e.body?.validationErrors) {
        showToast(t('CSV_UPLOAD_FAILED'), null, 'error');
        return;
      }

      const groupedErrors = e.body.validationErrors.reduce((acc, error) => {
        const key = isNaN(parseInt(error.row)) ? 'global' : error.row;
        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push({
          message: error.message,
          ...(error.cause && { cause: error.cause }),
        });

        return acc;
      }, {});

      setErrors(groupedErrors);
      clearFiles();
    } finally {
      setLoading(false);
    }
  }

  const clearFiles = () => {
    fileUploaderRef.current.clearFiles();
    setFiles([]);
  };

  const close = () => {
    setIsOpen(false);
    onClose?.();
  };

  const renderErrors = () => {
    if (!errors) {
      return null;
    }

    return (
      <Alert
        type="error"
        title={`${t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES')}:`}
        canClose
        onClose={() => setErrors(null)}
        className="mb-4"
      >
        {Object.keys(errors).map((row) => (
          <div className="mb-4" key={row}>
            <strong className="mb-2">{rowErrorHeader(row, t)}</strong>
            <ul>
              {errors[row].map((error, i) => (
                <li key={i}>{t(error.message)}</li>
              ))}
            </ul>
          </div>
        ))}
      </Alert>
    );
  };

  return (
    <Modal title={t('UPLOAD_LANE_RATES')} isOpen={isOpen} onClose={close}>
      <ModalBody>
        <Label className="mb-1">{t('LANE_RATES_CSV')}</Label>

        {renderErrors()}

        <FileUploader
          fileTypes={CSV_FILE_TYPES}
          fileTypesLabel={t('CSV')}
          onChange={(files) => setFiles(files)}
          ref={fileUploaderRef}
        />
      </ModalBody>

      <ModalActions
        primaryAction={{
          label: t('UPLOAD'),
          onClick: uploadLaneRates,
          disabled: loading || !files.length,
        }}
        secondaryActions={[
          {
            label: t('CANCEL'),
            onClick: close,
          },
        ]}
      />
    </Modal>
  );
};

LaneRatesCsvUploadModal.propTypes = {
  onClose: PropTypes.func,
};
